import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useKeyedState } from "@/hooks/useKeyedState";
import Link from "@/components/DashboardLink";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { X } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { githubWebUrl, useGithubWebOrigin } from "@/hooks/useGithubWebOrigin";
import { useSupportDrawerRedirect } from "@/hooks/useSupportDrawerState";
import { isCodesmithPath, settingsPagePath } from "./useActiveApp";
export function useSidebarGlobalBehaviors() {
  const $ = _c(8);
  const router = useRouter();
  const {
    activeNamedOrg,
    activeGithubOrgBlocked,
    activeGithubOrgSuspendedInstallation
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = router.query.org;
  const currentOrg = (typeof urlOrg === "string" ? urlOrg : undefined) || activeNamedOrg?.orgName;
  const gPrefixActiveRef = useRef(false);
  const pendingSecondKeyRef = useRef(null);
  useSupportDrawerRedirect();
  let t0;
  let t1;
  if ($[0] !== currentOrg || $[1] !== router) {
    t0 = () => {
      const clearPendingSecondKey = () => {
        const pending = pendingSecondKeyRef.current;
        if (!pending) {
          return;
        }
        document.removeEventListener("keydown", pending.handler);
        clearTimeout(pending.timeoutId);
        pendingSecondKeyRef.current = null;
      };
      const handleKeyDown = event => {
        if (isTypingTarget(event.target)) {
          return;
        }
        if (event.key !== "g" || event.metaKey || event.ctrlKey || event.altKey) {
          return;
        }
        clearPendingSecondKey();
        gPrefixActiveRef.current = true;
        const shortcuts = {
          a: currentOrg ? `/${currentOrg}/analytics` : "/analytics",
          w: currentOrg ? `/${currentOrg}/runs/workflows` : "/runs/workflows",
          j: currentOrg ? `/${currentOrg}/runs/jobs` : "/runs/jobs",
          l: currentOrg ? `/${currentOrg}/logs` : "/logs",
          d: currentOrg ? `/${currentOrg}/docker-analytics` : "/docker-analytics",
          s: currentOrg ? `/${currentOrg}/stickydisks` : "/stickydisks",
          u: currentOrg ? `/${currentOrg}/usage` : "/usage",
          t: settingsPagePath(currentOrg, isCodesmithPath(router.pathname)),
          m: currentOrg ? `/${currentOrg}/monitors` : "/monitors"
        };
        const handleSecondKey = secondEvent => {
          if (secondEvent.key !== "g") {
            gPrefixActiveRef.current = false;
          }
          const isTyping = isTypingTarget(secondEvent.target);
          const hasModifier = secondEvent.metaKey || secondEvent.ctrlKey || secondEvent.altKey;
          const route = isTyping || hasModifier ? undefined : shortcuts[secondEvent.key];
          if (route) {
            secondEvent.stopImmediatePropagation();
            router.push(route);
          }
          clearPendingSecondKey();
        };
        document.addEventListener("keydown", handleSecondKey);
        const timeoutId = setTimeout(() => {
          gPrefixActiveRef.current = false;
          clearPendingSecondKey();
        }, 2000);
        pendingSecondKeyRef.current = {
          handler: handleSecondKey,
          timeoutId
        };
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        clearPendingSecondKey();
      };
    };
    t1 = [currentOrg, router];
    $[0] = currentOrg;
    $[1] = router;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== activeGithubOrgBlocked || $[5] !== activeGithubOrgSuspendedInstallation || $[6] !== currentOrg) {
    t2 = {
      activeGithubOrgBlocked,
      activeGithubOrgSuspendedInstallation,
      currentOrg,
      gPrefixActiveRef
    };
    $[4] = activeGithubOrgBlocked;
    $[5] = activeGithubOrgSuspendedInstallation;
    $[6] = currentOrg;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function SidebarBlockedOrgBanner(t0) {
  const $ = _c(15);
  const {
    activeGithubOrgBlocked,
    suspendedInstallation,
    currentOrg
  } = t0;
  const githubWebOrigin = useGithubWebOrigin();
  const warningKey = suspendedInstallation ? `suspended:${suspendedInstallation.id}` : activeGithubOrgBlocked ? `blocked:${currentOrg ?? ""}` : null;
  const [bannerDismissed, setBannerDismissed] = useKeyedState(warningKey, false);
  if (!warningKey || bannerDismissed) {
    return null;
  }
  let t1;
  if ($[0] !== githubWebOrigin || $[1] !== suspendedInstallation) {
    t1 = suspendedInstallation && githubWebUrl(githubWebOrigin, suspendedInstallation.account.type === "User" ? `settings/installations/${suspendedInstallation.id}` : `organizations/${encodeURIComponent(suspendedInstallation.account.login)}/settings/installations/${suspendedInstallation.id}`);
    $[0] = githubWebOrigin;
    $[1] = suspendedInstallation;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const unsuspendUrl = t1;
  let t2;
  if ($[3] !== currentOrg || $[4] !== suspendedInstallation || $[5] !== unsuspendUrl) {
    t2 = suspendedInstallation ? <div className="flex-1 text-center">The Blacksmith GitHub App is suspended for{" "}{suspendedInstallation.account.login}, so jobs and [code]smith won't run.{" "}{suspendedInstallation.account.type === "User" ? "The account owner" : "An organization owner"}{" "}can unsuspend it in{" "}{unsuspendUrl ? <a href={unsuspendUrl} target="_blank" rel="noopener noreferrer" className="cursor-pointer underline hover:no-underline">GitHub settings</a> : "GitHub settings"}.</div> : <div className="flex-1 text-center">This account requires additional verification. <Link href={currentOrg ? `/${currentOrg}/support` : "/support"} className="cursor-pointer underline hover:no-underline">Contact support for help</Link></div>;
    $[3] = currentOrg;
    $[4] = suspendedInstallation;
    $[5] = unsuspendUrl;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== setBannerDismissed) {
    t3 = () => setBannerDismissed(true);
    $[7] = setBannerDismissed;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X className="size-4" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3) {
    t5 = <Button onClick={t3} className="size-8 cursor-pointer rounded-md bg-white/10 p-0 transition-colors" variant="subtle" aria-label="Dismiss message">{t4}</Button>;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <div className="fixed left-0 top-0 z-40 flex h-16 w-full items-center bg-red-500/80 text-sm text-white backdrop-blur-xs lg:text-base"><div className="flex w-full items-center justify-between px-4">{t2}{t5}</div></div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}